import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Check } from "lucide-react";
import { Section, SectionHeading, Reveal } from "./Primitives";
import { PRODUCTS } from "../../data/content";
import { useSiteContent } from "../../context/SiteContentContext";
import { getIcon } from "../../lib/icons";
import { ProductSkeleton } from "./Skeletons";

const ProductCard = ({ p, index }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [30, -30]);
  const Icon = getIcon(p.icon);

  return (
    <Reveal delay={index * 0.1} className="group">
      <div ref={ref} className="glass rounded-3xl overflow-hidden h-full flex flex-col">
        {/* Spotlight product frame */}
        <div className="relative h-56 md:h-64 overflow-hidden">
          <motion.img
            style={{ y }}
            src={p.image}
            alt={p.name}
            loading="lazy"
            className="absolute inset-0 h-[120%] w-full object-cover brightness-[0.55] saturate-[0.9] transition-all duration-700 group-hover:brightness-90 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-void via-void/40 to-transparent" />
          <div className="absolute top-5 left-5 inline-flex items-center gap-2 rounded-full bg-black/40 backdrop-blur-md border border-white/10 px-3 py-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-brand" />
            <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-white/80">{p.tag}</span>
          </div>
        </div>

        <div className="p-8 md:p-10 flex-1 flex flex-col">
          <div className="flex items-start gap-4">
            <div className="flex items-center justify-center h-11 w-11 rounded-xl bg-brand/10 border border-brand/20 text-brand shrink-0">
              <Icon className="h-5 w-5" strokeWidth={1.6} />
            </div>
            <div>
              <h3 className="font-heading text-xl md:text-2xl font-medium text-white leading-tight">{p.name}</h3>
              <p className="mt-2 text-sm md:text-base text-muted-foreground">{p.desc}</p>
            </div>
          </div>

          <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {p.features.map((f) => (
              <li key={f} className="flex items-center gap-2.5 text-sm text-white/80">
                <span className="flex items-center justify-center h-5 w-5 rounded-md bg-brand/15 text-brand shrink-0">
                  <Check className="h-3 w-3" strokeWidth={3} />
                </span>
                {f}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Reveal>
  );
};

export const ProductShowcase = () => {
  const { products, loading } = useSiteContent();
  return (
    <Section id="products" glow>
      <SectionHeading overline={PRODUCTS.overline} title={PRODUCTS.title} intro={PRODUCTS.intro} />
      <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
        {loading
          ? Array.from({ length: 2 }).map((_, i) => <ProductSkeleton key={i} />)
          : products.map((item, i) => <ProductCard key={item.id} p={item.data} index={i} />)}
      </div>
    </Section>
  );
};

export default ProductShowcase;
